import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faChartLine,
  faTag,
  faLocationDot,
  faVenusMars,
  faUsersViewfinder,
  faMobileScreenButton,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { FilterAIResponse } from "./FilterAI";
import "./AppliedFIlterChips.scss";

const AUDIENCE_LABELS: Record<string, string> = {
  babies: "0-4 (Baby men and women)",
  children: "13-17 (Teen men and women)",
  youngMen: "18-24 (Young men)",
  youngWomen: "18-24 (Young women)",
  women: "25-44 (Adult women)",
  men: "25-44 (Adult men)",
  oldWomen: "45+ (Mature women)",
  oldMen: "45+ (Mature men)",
};

const PLATFORM_LABELS: Record<string, string> = {
  instagram: "Instagram",
  tiktok: "TikTok",
};

const FILTER_CONFIG: Record<
  keyof FilterAIResponse,
  {
    icon: any;
    label: (v: string) => string;
  }
> = {
  minAmountFollowers: {
    icon: faUsers,
    label: (v: string) => `Min Followers: ${formatFollowers(v)}`,
  },
  minAmountEngagement: {
    icon: faChartLine,
    label: (v: string) => `Min Engagement: ${v}%`,
  },
  categories: {
    icon: faTag,
    label: (v: string) => v,
  },
  locations: {
    icon: faLocationDot,
    label: (v: string) => v,
  },
  gender: {
    icon: faVenusMars,
    label: (v: string) => v.charAt(0).toUpperCase() + v.slice(1),
  },
  audiences: {
    icon: faUsersViewfinder,
    label: (v: string) => AUDIENCE_LABELS[v] || v,
  },
  platform: {
    icon: faMobileScreenButton,
    label: (v: string) =>
      PLATFORM_LABELS[v] || v.charAt(0).toUpperCase() + v.slice(1),
  },
  sortField: {
    icon: faChartLine,
    label: (v: string) => `Sort By: ${v.charAt(0).toUpperCase() + v.slice(1)}`,
  },
  sortDirection: {
    icon: faChartLine,
    label: (v: string) =>
      `Sort Direction: ${v.charAt(0).toUpperCase() + v.slice(1)}`,
  },
};

function formatFollowers(val: string) {
  const n = Number(val);
  if (isNaN(n)) return val;
  if (n >= 1000 && n < 1000000) return `${Math.round(n / 1000)}K`;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  return n;
}

function getActiveFilters(search: string) {
  const params = new URLSearchParams(search);
  const filters: Array<{
    key: string;
    value: string;
  }> = [];

  if (params.has("platform")) {
    const platformString = params.get("platform") || "";
    const platforms = platformString.split(";").filter((p) => p.trim() !== "");
    platforms.forEach((platform) => {
      filters.push({ key: "platform", value: platform });
    });
  }

  if (params.has("minAmountFollowers")) {
    filters.push({
      key: "minAmountFollowers",
      value: params.get("minAmountFollowers") || "",
    });
  }

  if (params.has("minAmountEngagement")) {
    filters.push({
      key: "minAmountEngagement",
      value: params.get("minAmountEngagement") || "",
    });
  }

  if (params.has("categories")) {
    const categories =
      params
        .get("categories")
        ?.split(";")
        .filter((cat) => cat.trim() !== "") ?? [];
    categories.forEach((cat) => {
      if (cat.includes("|")) {
        const parts = cat.split("|");
        filters.push({ key: "categories", value: parts[parts.length - 1] });
        return;
      }
      filters.push({ key: "categories", value: cat });
    });
  }

  if (params.has("audiences")) {
    const audiencesString = params.get("audiences") || "";
    const audiences = audiencesString
      .split(";")
      .filter((aud) => aud.trim() !== "");
    audiences.forEach((aud) => {
      filters.push({ key: "audiences", value: aud });
    });
  }

  if (params.has("locations")) {
    const locations = params.get("locations") || "";
    const locationsArray = locations
      .split(";")
      .filter((loc) => loc.trim() !== "");
    locationsArray.forEach((loc) => {
      if (loc.includes("|")) {
        const parts = loc.split("|");
        filters.push({ key: "locations", value: parts[parts.length - 1] });
        return;
      }
      filters.push({ key: "locations", value: loc });
    });
  }

  if (params.has("gender")) {
    const genderString = params.get("gender") || "";
    const genders = genderString.split(";").filter((g) => g.trim() !== "");
    genders.forEach((gender) => {
      filters.push({ key: "gender", value: gender });
    });
  }

  if (params.has("sortField")) {
    filters.push({
      key: "sortField",
      value: params.get("sortField") || "",
    });
  }

  if (params.has("sortDirection")) {
    filters.push({
      key: "sortDirection",
      value: params.get("sortDirection") || "",
    });
  }
  return filters;
}

const AppliedFIlterChips: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const filters = getActiveFilters(location.search);

  const removeFilter = (filter: { key: string; value: string }) => {
    const params = new URLSearchParams(location.search);

    const lastSegment = (v: string) => {
      if (!v) return v;
      const parts = v.split("|");
      return parts[parts.length - 1];
    };

    if (filter.key === "categories") {
      const categoriesString = params.get("categories") || "";
      const categories = categoriesString.split(";").filter((cat) => {
        const trimmed = cat.trim();
        if (trimmed === "") return false;
        if (trimmed.includes("|")) {
          const parts = trimmed.split("|");
          return (
            parts[parts.length - 1] !== filter.value && trimmed !== filter.value
          );
        }
        return trimmed !== filter.value;
      });
      params.delete("categories");
      if (categories.length > 0) {
        params.set("categories", categories.join(";"));
      }
    } else if (filter.key === "audiences") {
      const audiencesString = params.get("audiences") || "";
      const audiences = audiencesString
        .split(";")
        .filter((aud) => aud.trim() !== "" && aud !== filter.value);
      params.delete("audiences");
      if (audiences.length > 0) {
        params.set("audiences", audiences.join(";"));
      }
    } else if (filter.key === "gender") {
      const genderString = params.get("gender") || "";
      const genders = genderString
        .split(";")
        .filter((g) => g.trim() !== "" && g !== filter.value);
      params.delete("gender");
      if (genders.length > 0) {
        params.set("gender", genders.join(";"));
      }
    } else if (filter.key === "locations") {
      const locations = params.get("locations") || "";
      const locationsArray = locations
        .split(";")
        .filter((loc) => loc.trim() !== "");
      const filteredLocations = locationsArray.filter((loc) => {
        const trimmed = loc.trim();
        if (trimmed === "") return false;
        if (trimmed.includes("|")) {
          const parts = trimmed.split("|");
          return (
            parts[parts.length - 1] !== filter.value && trimmed !== filter.value
          );
        }
        return trimmed !== filter.value;
      });
      params.delete("locations");
      if (filteredLocations.length > 0) {
        params.set("locations", filteredLocations.join(";"));
      }
    } else if (filter.key === "platform") {
      const platformString = params.get("platform") || "";
      const platforms = platformString
        .split(";")
        .filter((p) => p.trim() !== "" && p !== filter.value);
      params.delete("platform");
      if (platforms.length > 0) {
        params.set("platform", platforms.join(";"));
      }
    } else {
      let removed = false;
      for (const key of Array.from(params.keys())) {
        const values = params.get(key) || "";
        const valuesArray = values.split(";").filter((v) => v.trim() !== "");
        const kept = valuesArray.filter(
          (v) => lastSegment(v) !== filter.value && v !== filter.value
        );
        if (kept.length !== values.length) {
          removed = true;
          params.delete(key);
          kept.forEach((v) => params.append(key, v));
        }
      }

      if (!removed) {
        params.delete(filter.key);
      }
    }
    const searchString = params.toString();
    navigate(`${location.pathname}${searchString ? `?${searchString}` : ""}`);
  };

  if (filters.length === 0) return null;

  return (
    <div className="applied-filters-chips">
      {filters.map((filter, idx) => {
        const config = FILTER_CONFIG[filter.key as keyof typeof FILTER_CONFIG];
        if (!config) return null;
        return (
          <div
            className="applied-filter-chip"
            key={filter.key + filter.value + idx}
          >
            <FontAwesomeIcon
              icon={config.icon}
              className="applied-filter-chip__icon"
            />
            <span className="applied-filter-chip__label">
              {config.label(filter.value)}
            </span>
            <button
              className="applied-filter-chip__remove"
              onClick={() => removeFilter(filter)}
              aria-label="Remove filter"
            >
              x
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default AppliedFIlterChips;
